import React from 'react';
import { Container, Row, Col, Card, Badge, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartLine,
    faExclamationTriangle, faLightbulb, faRocket, faArrowRight,
    faDesktop, faServer, faDatabase, faChartBar, faRandom,
    faClock, faPercent, faShoppingCart
} from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import SEO from '../SEO/SEO';

import pyramid from "../../assets/images/use-cases/CustomerSupport-Process.png";
import vis from "../../assets/images/use-cases/CustomerSupport-Dashboard.png";

const CustomerSupportResolution = () => {
    return (
        <>
            <SEO
                title="Customer Support Resolution: AI Digital Twin for Retail Contact Centres | Nebulyx AI"
                description="Simulate changes to your retail support workflow to improve first-contact resolution and reduce handle time. Test routing rules and triage automation offline before deployment."
                keywords="AI digital twin, customer support optimisation, contact centre simulation, FCR improvement, retail support, ticket routing"
                canonicalUrl="https://nebulyx.ai/use-cases/customer-support-resolution"
                ogType="article"
            />

            <article className="use-case-page">
                {/* Hero */}
                <header className="use-case-hero">
                    <Container>
                        <Row>
                            <Col className={"w-100"}>
                                <div className={"w-100 text-center"}>
                                    <div >
                                        <Badge className="sector-badge m-1 mb-4 h-100">
                                            <FontAwesomeIcon icon={faShoppingCart}/>
                                            &nbsp;
                                            Retail
                                        </Badge>
                                        <h1 className="use-case-title">Question:&nbsp;How do we improve first-contact resolution without adding headcount?</h1>
                                    </div>
                                </div>
                                <div>
                                    <p className="use-case-subtitle w-100 text-center">
                                        Simulate changes to triage rules, skill-based routing, and escalation thresholds offline, improving resolution rates and reducing handle time before peak trading periods.
                                    </p>
                                </div>
                            </Col>
                        </Row>

                        <Row className="hero-stats-inline ">
                            <Col className="stat-item-inline">
                                <FontAwesomeIcon icon={faPercent} className="stat-icon"/>
                                <span className="stat-value">65%→84%</span>
                                <span className="stat-label">First-Contact Resolution</span>
                            </Col>
                            <Col  className="stat-item-inline">
                                <FontAwesomeIcon icon={faClock} className="stat-icon"/>
                                <span className="stat-value">35%</span>
                                <span className="stat-label">Handle Time Reduction</span>
                            </Col>
                            <Col  className="stat-item-inline">
                                <FontAwesomeIcon icon={faChartLine} className="stat-icon"/>
                                <span className="stat-value">+12 pts</span>
                                <span className="stat-label">CSAT Improvement</span>
                            </Col>
                        </Row>

                    </Container>
                </header>

                {/* Executive Summary */}
                <section className="exec-summary-section">
                    <Container>
                        <h2 className="section-title">Executive Summary</h2>
                        <Row className="g-4">
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span className={"card-icon-wrapper problem me-1"}><FontAwesomeIcon
                                            sty icon={faExclamationTriangle}/></span>The Problem</h3>
                                        <ul className="summary-list">
                                            <li>High escalation rates on order and returns queries</li>
                                            <li>Seasonal spikes overwhelm existing team capacity</li>
                                            <li>No way to test routing changes before peak trading</li>
                                            <li>Agent skills not matched to query complexity</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span
                                            className={"card-icon-wrapper solution me-1"}><FontAwesomeIcon
                                            sty icon={faLightbulb}/></span>The Solution</h3>
                                        <ul className="summary-list">
                                            <li>Model support workflow as connected process steps</li>
                                            <li>Overlay resolution and escalation analytics</li>
                                            <li>Simulate routing and triage changes offline</li>
                                            <li>Generate optimised workflow recommendations</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span
                                            className={"card-icon-wrapper benefits me-1"}><FontAwesomeIcon
                                            sty icon={faRocket}/></span>The Benefits</h3>
                                        <ul className="summary-list">
                                            <li>Test peak-season scenarios before Black Friday</li>
                                            <li>Identify chatbot deflection opportunities</li>
                                            <li>Optimise skill-based routing rules</li>
                                            <li>Predict staffing needs by query type</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                </section>

                {/* Process Steps */}
                <section className="content-section process-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin Process</h2>
                        <p className="text-center text-muted mb-4">
                            Connect support data, model each workflow step, overlay resolution analytics, then simulate process changes to find the optimal configuration.
                        </p>
                        <div className="process-steps-container">
                            <div className="process-step-item">
                                <div className="step-number">1</div>
                                <div className="step-content">
                                    <strong>Connect Data</strong>
                                    <span>CRM history, ticketing events, agent performance, knowledge base usage, e-commerce orders</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">2</div>
                                <div className="step-content">
                                    <strong>Model the Workflow</strong>
                                    <span>Map ticket intake, triage, routing, resolution, and escalation paths</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">3</div>
                                <div className="step-content">
                                    <strong>Overlay Analytics</strong>
                                    <span>FCR by category, handle time, escalation rates, agent utilisation, CSAT correlation</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">4</div>
                                <div className="step-content">
                                    <strong>Scenario Analysis</strong>
                                    <span>Simulate routing rules, triage automation, escalation thresholds, staffing levels</span>
                                </div>
                            </div>
                        </div>
                    </Container>
                </section>

                {/* AI Digital Twin - Two Sections */}
                <section className="content-section twin-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin</h2>
                        <Row className="g-4">
                            {/* Back-end Architecture */}
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon backend">
                                                <FontAwesomeIcon icon={faServer}/>
                                            </div>
                                            <h3>Digital Twin Process Flow</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Connects CRM, ticketing, agent, and e-commerce systems to model the end-to-end support journey.
                                            Each step captures resolution rates, handle times, and escalation triggers. Enables offline simulation of workflow changes
                                            so teams can optimise routing and triage before peak retail periods.</p>
                                        <div className="image-parent">
                                            <img className={"image-child-span w-100 h-100"} src={pyramid} alt=""/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Front-end UI */}
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon frontend">
                                                <FontAwesomeIcon icon={faDesktop}/>
                                            </div>
                                            <h3>Support Simulation Workbench</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Visualises ticket flow through each support stage with real-time queue depths and agent utilisation.
                                            Allows teams to adjust routing rules, test chatbot deflection scenarios, and simulate Black Friday volumes, then
                                            see predicted impact on FCR, handle time, and CSAT before committing changes.</p>
                                        <div className="image-parent">
                                            <img className={"image-child-span w-100 h-100"} src={vis} alt=""/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                        {/* Twin Components */}
                        <Row className="g-4 mt-4">
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faDatabase}/>
                                            </div>
                                            <h4>Data Analysed</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Customer Account History (CRM)</li>
                                            <li>Ticket Events & Timestamps</li>
                                            <li>Agent Skills & Availability</li>
                                            <li>Order & Returns Data</li>
                                            <li>Knowledge Base Usage</li>
                                            <li>CSAT Survey Responses</li>
                                            <li>Channel Source (Chat/Email/Phone)</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faChartBar}/>
                                            </div>
                                            <h4>AI / Data Products</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>First-contact resolution prediction</li>
                                            <li>Escalation probability scoring</li>
                                            <li>Handle time estimation</li>
                                            <li>Agent-skill match optimisation</li>
                                            <li>Chatbot deflection candidates</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faRandom}/>
                                            </div>
                                            <h4>Scenarios Tested</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Black Friday volume surge (+300%)</li>
                                            <li>Auto-resolve order tracking queries</li>
                                            <li>Skill-based routing by category</li>
                                            <li>Escalation threshold adjustments</li>
                                            <li>Chatbot deflection for returns</li>
                                            <li>Staffing levels by time slot</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Discovery */}
                        <div className="discovery-highlight mt-5">
                            <Row className="align-items-center">
                                <Col lg={8}>
                                    <h3>The Potential Discovery</h3>
                                    <p>
                                        By modelling the support workflow and simulating thousands of ticket routing variations offline,
                                        teams could discover that implementing skill-based routing for order queries, combined with
                                        chatbot deflection for "where is my order" requests,
                                        <strong> could improve FCR from 65% to 84%</strong>.
                                        Simulation may reveal that 40% of escalations stem from misrouted tickets,
                                        an insight that enables targeted routing fixes before the peak trading season.
                                    </p>
                                </Col>
                                <Col lg={4}>
                                    <div className="discovery-stat">
                                        <span className="discovery-label">Potential Strategy</span>
                                        <span className="discovery-value">Skill Routing + Chatbot</span>
                                        <span className="discovery-note">FCR 65%→84%, tested offline</span>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </Container>
                </section>

                {/* CTA */}
                <section className="cta-section">
                    <Container>
                        <div className="cta-content">
                            <h2>Ready to Optimise Support Before Peak Season?</h2>
                            <p>
                                If escalation rates are climbing and you need to prepare for seasonal demand,
                                let's build your Digital Twin together.
                            </p>
                            <div className="cta-buttons">
                                <Button
                                    variant="light"
                                    size="lg"
                                    href="https://cal.eu/jon-cooke/discovery-call"
                                    className="me-3 mb-2"
                                >
                                    Book a Discovery Call
                                    <FontAwesomeIcon icon={faArrowRight} className="ms-2"/>
                                </Button>
                                <Link to="/use-cases">
                                    <Button variant="outline-light" size="lg" className="mb-2">
                                        View More Use Cases
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </Container>
                </section>
            </article>
        </>
    );
};

export default CustomerSupportResolution;